import type { ExtractedItem } from './ocr-client';
import type { ParentDecision, PromotionInput } from './promote';
import { ensureAcademicTheme } from '../ui/theme';
import { academicButton } from '../ui/components';
import { renderFigure } from './figure';

/**
 * 家長逐題確認 OCR 結果。
 *
 * 視覺 = 學術風格設計系統(boss 2026-06-08;見 wiki page-470f59c0)。被 upload.ts /
 * photo-upload.ts append 進它們的 stage,所以這裡不套整頁 `pageShell`(避免雙標題列),
 * 而是套同一套 token / class:紙片卡(`.ef-card`)+ 學術按鈕(`academicButton`)。
 * 每道題一張紙片,四個判斷選項用紙色描邊 pill,選中態用學術綠;「完成」用主按鈕。
 *
 * 行為 / class / 流程完全不變(`.parent-confirm`、`.confirm-card`、`.cf-kp`/`.cf-prompt`/
 * `.cf-answer`、`[data-d]` 判斷鈕、`.active` 選中態、`#confirm-all` 完成鈕、render 後
 * `root.remove()` → onDone),測試依賴的節點全保留。
 */
export function renderParentConfirm(
  container: HTMLElement,
  items: ExtractedItem[],
  onDone: (inputs: PromotionInput[]) => void,
): void {
  ensureAcademicTheme();
  ensureParentConfirmStyle();

  const root = document.createElement('div');
  root.className = 'parent-confirm';
  container.appendChild(root);
  // 預設帶入自動批改結果:答錯→「需修正」、其餘→「對」。家長只需修正例外。
  const decisions: ParentDecision[] = items.map(defaultDecision);

  // 「➕ 補一題」:OCR 漏題時家長手動補(boss 2026-06-09「應該是 15 題」→ 補最後一題)。
  // 草稿存閉包,decision 點擊 re-render 時不丟輸入。
  let addOpen = false;
  const draft = { subject: 'math' as ExtractedItem['subject'], prompt: '', answer: '', student: '' };

  function addManualItem(): void {
    const prompt = draft.prompt.trim();
    if (!prompt) return; // 沒題目不加
    const answer = draft.answer.trim();
    const student = draft.student.trim();
    const norm = (s: string): string => s.replace(/\s+/g, '').toLowerCase();
    const isCorrect = student === '' ? null : norm(student) === norm(answer);
    const item: ExtractedItem = {
      subject: draft.subject,
      kp: `${draft.subject}.manual`,
      kind: 'fill',
      prompt,
      correctAnswer: answer,
      explanation: '',
      studentAnswer: student,
      isCorrect,
    };
    items.push(item);
    decisions.push(defaultDecision(item));
    draft.prompt = '';
    draft.answer = '';
    draft.student = '';
    addOpen = false;
    render();
  }

  function mkField(labelText: string, value: string, onInput: (v: string) => void): HTMLElement {
    const l = document.createElement('label');
    l.className = 'cf-add-field';
    const span = document.createElement('span');
    span.className = 'cf-add-label';
    span.textContent = labelText;
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'cf-add-input';
    input.value = value;
    input.addEventListener('input', () => onInput(input.value));
    l.append(span, input);
    return l;
  }

  function buildAddSection(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'cf-add';
    const toggle = academicButton({
      label: addOpen ? '✕ 取消補題' : '＋ 補一題(OCR 漏的)',
      variant: 'secondary',
      onClick: () => { addOpen = !addOpen; render(); },
    });
    toggle.id = 'cf-add-toggle';
    wrap.appendChild(toggle);
    if (!addOpen) return wrap;

    const form = document.createElement('div');
    form.className = 'cf-add-form';
    const subjWrap = document.createElement('label');
    subjWrap.className = 'cf-add-field';
    const subjSpan = document.createElement('span');
    subjSpan.className = 'cf-add-label';
    subjSpan.textContent = '科目';
    const subjSel = document.createElement('select');
    subjSel.className = 'cf-add-input';
    const SUBJECTS: [ExtractedItem['subject'], string][] = [
      ['math', '數學'], ['chinese', '國語'], ['english', '英文'], ['nature', '自然'], ['social', '社會'],
    ];
    for (const [v, label] of SUBJECTS) {
      const o = document.createElement('option');
      o.value = v; o.textContent = label;
      if (v === draft.subject) o.selected = true;
      subjSel.appendChild(o);
    }
    subjSel.addEventListener('change', () => { draft.subject = subjSel.value as ExtractedItem['subject']; });
    subjWrap.append(subjSpan, subjSel);

    const addBtn = academicButton({ label: '加入這一題', variant: 'primary', onClick: addManualItem });
    addBtn.id = 'cf-add-submit';
    form.append(
      subjWrap,
      mkField('題目(OCR 漏掉的那一題)', draft.prompt, (v) => { draft.prompt = v; }),
      mkField('正解', draft.answer, (v) => { draft.answer = v; }),
      mkField('小孩的作答(可空白)', draft.student, (v) => { draft.student = v; }),
      addBtn,
    );
    wrap.appendChild(form);
    return wrap;
  }

  function render(): void {
    const graded = items.some((it) => it.isCorrect === true || it.isCorrect === false);
    const correctN = items.filter((it) => it.isCorrect === true).length;
    const wrongN = items.filter((it) => it.isCorrect === false).length;
    const headTitle = graded ? '自動批改結果' : '家長確認 OCR 結果';
    const headHelp = graded
      ? `已自動批改:共 ${items.length} 題,${correctN} 對、${wrongN} 錯。看一下、改掉判錯的,再送出(答錯的會進錯題本)。`
      : '請逐題確認小孩的答題狀況:';
    root.innerHTML = `
      <h1 class="parent-confirm__title">${escapeHtml(headTitle)}</h1>
      <p class="parent-confirm__help">${escapeHtml(headHelp)}</p>
      <div class="confirm-list"></div>
    `;
    const list = root.querySelector('.confirm-list')!;
    items.forEach((it, i) => {
      const card = document.createElement('div');
      card.className = 'confirm-card ef-card';
      const isGraded = it.isCorrect !== undefined || it.studentAnswer !== undefined;
      const v = verdictMeta(it.isCorrect);
      const verdictSpan = isGraded ? `<span class="cf-verdict ${v.cls}">${v.label}</span>` : '';
      const studentLine = isGraded
        ? `<div class="cf-student">小孩寫:${escapeHtml((it.studentAnswer ?? '') === '' ? '(空白)' : String(it.studentAnswer))}</div>`
        : '';
      card.innerHTML = `
        <div class="cf-kp">${escapeHtml(it.subject)} / ${escapeHtml(it.kp)}${verdictSpan}</div>
        <div class="cf-prompt">${escapeHtml(it.prompt)}</div>
        ${studentLine}
        <div class="cf-answer">正解:${escapeHtml(String(it.kind === 'mcq' ? (it.options?.[it.correctAnswer as number] ?? it.correctAnswer) : it.correctAnswer))}</div>
        <div class="cf-actions">
          <button class="cf-choice" data-d="correct">對</button>
          <button class="cf-choice" data-d="wrong">需修正</button>
          <button class="cf-choice" data-d="careless">粗心</button>
          <button class="cf-choice" data-d="discard">丟棄</button>
        </div>
      `;
      // 系統重繪的參考圖(統計表/長條圖/象形圖/時鐘)— OCR 讀到圖表資料時,
      // 在題目下方生成一張乾淨的圖,取代裁糊照片(boss 2026-06-10)。
      if (it.figure) {
        try {
          const promptEl = card.querySelector('.cf-prompt');
          const figEl = renderFigure(it.figure);
          if (promptEl && promptEl.parentNode) {
            promptEl.parentNode.insertBefore(figEl, promptEl.nextSibling);
          } else {
            card.appendChild(figEl);
          }
        } catch { /* 圖表生成失敗不影響整題 */ }
      }

      // Highlight current decision
      const cur = decisions[i];
      card.querySelectorAll('.cf-actions button').forEach(b => {
        if ((b as HTMLElement).dataset.d === cur) b.classList.add('active');
        b.addEventListener('click', () => {
          decisions[i] = (b as HTMLElement).dataset.d as ParentDecision;
          render();
        });
      });
      list.appendChild(card);
    });

    // 補題區(完成鈕之前):OCR 漏的題在這裡手動補齊。
    root.appendChild(buildAddSection());

    const confirmBtn = academicButton({
      label: '完成,寫入弱點記錄',
      variant: 'primary',
      onClick: () => {
        const inputs: PromotionInput[] = items.map((item, i) => ({ item, decision: decisions[i] }));
        root.remove();
        onDone(inputs);
      },
    });
    confirmBtn.id = 'confirm-all';
    confirmBtn.classList.add('parent-confirm__submit');
    root.appendChild(confirmBtn);
  }
  render();
}

function escapeHtml(s: string): string {
  const d = document.createElement('div'); d.textContent = s; return d.innerHTML;
}

/**
 * 自動批改 → 預設家長判斷。答錯(`isCorrect===false`)預設「需修正」(會進錯題本);
 * 答對 / 讀不到 / 未批改一律預設「對」(不進錯題本,讀不到的交家長自己看)。
 * 家長可逐題改(含改成「粗心」)。
 */
function defaultDecision(it: ExtractedItem): ParentDecision {
  return it.isCorrect === false ? 'wrong' : 'correct';
}

/** 自動判讀徽章文字 / 樣式。 */
function verdictMeta(isCorrect: boolean | null | undefined): { label: string; cls: string } {
  if (isCorrect === true) return { label: '✓ 答對', cls: 'cf-verdict--ok' };
  if (isCorrect === false) return { label: '✗ 答錯', cls: 'cf-verdict--bad' };
  return { label: '? 未判讀', cls: 'cf-verdict--unknown' };
}

/** 確認卡 scoped 樣式(只用 ef- token,不碰全域 styles.css)。冪等注入。 */
const PARENT_CONFIRM_STYLE_ID = 'ef-parent-confirm-style';
function ensureParentConfirmStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PARENT_CONFIRM_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PARENT_CONFIRM_STYLE_ID;
  style.textContent = `
.parent-confirm { display:flex; flex-direction:column; gap:14px; color:var(--ef-ink-soft); font-family:var(--ef-sans); }
.parent-confirm__title { margin:0; font-family:var(--ef-serif); font-weight:700; font-size:22px; letter-spacing:0.5px; color:var(--ef-ink); }
.parent-confirm__help { margin:0; font-size:14px; line-height:1.6; color:var(--ef-sub); }
.parent-confirm .confirm-list { display:flex; flex-direction:column; gap:12px; }
.parent-confirm .confirm-card { gap:8px; cursor:default; }
.parent-confirm .confirm-card:hover { transform:none; box-shadow:var(--ef-shadow-card); border-color:var(--ef-line); }
.parent-confirm .cf-kp { font-size:12px; letter-spacing:1px; text-transform:uppercase; color:var(--ef-green-dark); font-weight:700; }
.parent-confirm .cf-prompt { font-family:var(--ef-serif); font-size:17px; line-height:1.5; color:var(--ef-ink); }
.parent-confirm .cf-student { font-size:14px; color:var(--ef-ink-soft); }
.parent-confirm .cf-answer { font-size:14px; color:var(--ef-sub); }
.parent-confirm .cf-verdict { display:inline-block; margin-left:8px; padding:2px 9px; border-radius:var(--ef-radius-pill); font-size:11px; font-weight:700; letter-spacing:0.5px; vertical-align:middle; }
.parent-confirm .cf-verdict--ok { background:rgba(31,111,92,0.12); color:var(--ef-green-dark); }
.parent-confirm .cf-verdict--bad { background:rgba(179,38,30,0.10); color:#b3261e; }
.parent-confirm .cf-verdict--unknown { background:var(--ef-paper); color:var(--ef-sub); border:1px solid var(--ef-line); }
.parent-confirm .cf-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top:4px; }
.parent-confirm .cf-choice {
  min-height:38px; padding:8px 16px; border:1px solid var(--ef-line); border-radius:var(--ef-radius-pill);
  background:var(--ef-paper-bright); color:var(--ef-ink-soft); font-family:var(--ef-sans); font-size:14px;
  font-weight:600; letter-spacing:0.5px; cursor:pointer;
  transition:background 0.16s ease, color 0.16s ease, border-color 0.16s ease;
  -webkit-tap-highlight-color:transparent; touch-action:manipulation;
}
.parent-confirm .cf-choice:hover { border-color:var(--ef-green); background:rgba(31,111,92,0.06); }
.parent-confirm .cf-choice:focus-visible { outline:3px solid rgba(31,111,92,0.4); outline-offset:2px; }
.parent-confirm .cf-choice.active { background:var(--ef-green); color:#fff; border-color:var(--ef-green); }
.parent-confirm .cf-choice[data-d="discard"].active { background:#b3261e; border-color:#b3261e; }
.parent-confirm__submit { align-self:flex-start; margin-top:4px; }
.parent-confirm .cf-add { display:flex; flex-direction:column; gap:12px; margin-top:4px; }
.parent-confirm .cf-add-form {
  display:flex; flex-direction:column; gap:10px; padding:14px 16px;
  background:var(--ef-paper-bright); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
}
.parent-confirm .cf-add-field { display:flex; flex-direction:column; gap:4px; }
.parent-confirm .cf-add-label { font-size:13px; font-weight:600; color:var(--ef-sub); }
.parent-confirm .cf-add-input {
  padding:9px 11px; font-size:15px; font-family:inherit; color:var(--ef-ink);
  background:var(--ef-paper-bright); border:1px solid var(--ef-line); border-radius:8px;
}
.parent-confirm .cf-add-input:focus-visible { outline:3px solid rgba(31,111,92,0.35); outline-offset:1px; border-color:var(--ef-green); }
@media (prefers-reduced-motion: reduce) {
  .parent-confirm .cf-choice { transition:none; }
}
`;
  document.head.appendChild(style);
}
